import React from "react";
import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "indian-five-year-plans-summary-first-to-twelfth",
  title: "Indian Five Year Plans: Complete Summary from First to Twelfth Plan (1951-2017)",
  description: "A comprehensive summary of all 12 Indian Five Year Plans, their objectives, the NITI Aayog transition, and key historical contexts for economics students.",
  pubDate: "2025-08-21",
  authorSlug: "dhairya-madaan",
  category: "subject-explainers",
  tags: ["indian economy", "five year plans", "economic history", "planning commission"],
  readMinutes: 18,
  heroEyebrow: "Indian Economy History"
};

export function Body() {
  return (
    <>
      <p>
        The history of independent India's economic development is intrinsically tied to its Five Year Plans. Starting in 1951 under the leadership of Prime Minister Jawaharlal Nehru, the Planning Commission steered India's growth trajectory for over six decades until it was replaced by the NITI Aayog in 2015. For Indian economics undergraduates and competitive exam aspirants, mastering the timeline, objectives, and outcomes of these plans is absolutely essential.
      </p>

      <h2>1. Why Was Planning Adopted in India?</h2>
      <p>
        When India gained independence in 1947, it inherited an impoverished economy, low agricultural productivity, virtually zero industrial base, and massive inequalities. Influenced by the success of Soviet planning and the Keynesian push for state intervention, Indian leaders opted for a mixed economy framework.
      </p>
      <ul>
        <li><strong>Nehruvian Vision:</strong> Democratic socialism where the state commands the "commanding heights" of the economy (heavy industries, infrastructure).</li>
        <li><strong>Feldman-Mahalanobis Model:</strong> Focused on prioritizing the capital goods sector to ensure long-term self-sustaining growth.</li>
        <li><strong>Resource Mobilization:</strong> Planning was seen as a way to rationally allocate scarce resources for maximum social benefit.</li>
      </ul>

      <h2>2. Detailed Summary of the Five Year Plans (1951-2017)</h2>

      <h3>First Five Year Plan (1951 - 1956)</h3>
      <ul>
        <li><strong>Prime Minister:</strong> Jawaharlal Nehru</li>
        <li><strong>Model:</strong> Harrod-Domar Model</li>
        <li><strong>Focus Area:</strong> Agriculture, price stability, power, and transport.</li>
        <li><strong>Key Highlights:</strong> Massive irrigation projects like Bhakra Nangal and Hirakud dams were initiated. Five IITs were established at the end of the plan.</li>
        <li><strong>Outcome:</strong> Successful. Target Growth: 2.1%. Actual Growth: 3.6%.</li>
      </ul>

      <h3>Second Five Year Plan (1956 - 1961)</h3>
      <ul>
        <li><strong>Prime Minister:</strong> Jawaharlal Nehru</li>
        <li><strong>Model:</strong> P.C. Mahalanobis Model (Heavy Industry)</li>
        <li><strong>Focus Area:</strong> Rapid industrialization, specifically heavy and basic industries.</li>
        <li><strong>Key Highlights:</strong> Steel plants set up at Bhilai, Durgapur, and Rourkela with foreign help. Coal production increased. This plan faced foreign exchange crisis.</li>
        <li><strong>Outcome:</strong> Moderately successful. Target Growth: 4.5%. Actual Growth: 4.3%.</li>
      </ul>

      <h3>Third Five Year Plan (1961 - 1966)</h3>
      <ul>
        <li><strong>Prime Minister:</strong> Jawaharlal Nehru / Lal Bahadur Shastri</li>
        <li><strong>Model:</strong> Gadgil Yojana</li>
        <li><strong>Focus Area:</strong> Self-reliance and agriculture (making India self-sufficient in foodgrains).</li>
        <li><strong>Key Highlights:</strong> The plan failed miserably due to the Sino-Indian War (1962), Indo-Pak War (1965), and severe droughts (1965-66). Rupee was devalued in 1966.</li>
        <li><strong>Outcome:</strong> Failure. Target Growth: 5.6%. Actual Growth: 2.8%.</li>
      </ul>

      <h3>Plan Holidays (1966 - 1969)</h3>
      <p>
        Due to the failure of the Third Plan, regular five-year planning was paused. Three Annual Plans were executed. The core focus was on agricultural revitalization, which set the stage for the <strong>Green Revolution</strong> (introduction of HYV seeds).
      </p>

      <h3>Fourth Five Year Plan (1969 - 1974)</h3>
      <ul>
        <li><strong>Prime Minister:</strong> Indira Gandhi</li>
        <li><strong>Focus Area:</strong> Growth with stability and progressive achievement of self-reliance.</li>
        <li><strong>Key Highlights:</strong> Nationalization of 14 major Indian banks (1969), Indo-Pak war (1971) and creation of Bangladesh, leading to massive refugee influx.</li>
        <li><strong>Outcome:</strong> Failure (due to external shocks and oil crisis). Target Growth: 5.7%. Actual Growth: 3.3%.</li>
      </ul>

      <h3>Fifth Five Year Plan (1974 - 1978)</h3>
      <ul>
        <li><strong>Prime Minister:</strong> Indira Gandhi / Morarji Desai</li>
        <li><strong>Model:</strong> D.P. Dhar</li>
        <li><strong>Focus Area:</strong> Poverty eradication (Garibi Hatao) and self-reliance.</li>
        <li><strong>Key Highlights:</strong> The Minimum Needs Programme was introduced. The plan was terminated a year early in 1978 by the newly elected Janata Party government (which introduced Rolling Plans).</li>
        <li><strong>Outcome:</strong> Successful. Target Growth: 4.4%. Actual Growth: 4.8%.</li>
      </ul>

      <h3>Sixth Five Year Plan (1980 - 1985)</h3>
      <ul>
        <li><strong>Prime Minister:</strong> Indira Gandhi</li>
        <li><strong>Focus Area:</strong> Economic liberalization, poverty eradication, technological self-reliance.</li>
        <li><strong>Key Highlights:</strong> Marked a shift away from strict Nehruvian socialism. Establishment of NABARD (1982) and EXIM Bank.</li>
        <li><strong>Outcome:</strong> Successful. Target Growth: 5.2%. Actual Growth: 5.7%.</li>
      </ul>

      <h3>Seventh Five Year Plan (1985 - 1990)</h3>
      <ul>
        <li><strong>Prime Minister:</strong> Rajiv Gandhi</li>
        <li><strong>Focus Area:</strong> Food, Work, and Productivity. Upgrading technology.</li>
        <li><strong>Key Highlights:</strong> Focus on rapid grain production, employment generation, and modernization. Jawahar Rozgar Yojana was launched.</li>
        <li><strong>Outcome:</strong> Successful. Target Growth: 5.0%. Actual Growth: 6.0%.</li>
      </ul>

      <h3>Annual Plans (1990 - 1992)</h3>
      <p>
        The Eighth Plan was delayed due to severe political instability at the center and the devastating Balance of Payments (BoP) crisis of 1991. The historic LPG (Liberalization, Privatization, Globalization) reforms were introduced under PM P.V. Narasimha Rao and FM Dr. Manmohan Singh.
      </p>

      <h3>Eighth Five Year Plan (1992 - 1997)</h3>
      <ul>
        <li><strong>Prime Minister:</strong> P.V. Narasimha Rao</li>
        <li><strong>Model:</strong> Rao and Manmohan Model (Indicative Planning)</li>
        <li><strong>Focus Area:</strong> Human resource development (education, health), opening the economy to free-market forces.</li>
        <li><strong>Key Highlights:</strong> Rapid economic growth post-liberalization. India became a member of the WTO in 1995.</li>
        <li><strong>Outcome:</strong> Highly successful. Target Growth: 5.6%. Actual Growth: 6.8%.</li>
      </ul>

      <h3>Ninth Five Year Plan (1997 - 2002)</h3>
      <ul>
        <li><strong>Prime Minister:</strong> Atal Bihari Vajpayee</li>
        <li><strong>Focus Area:</strong> Growth with social justice and equity.</li>
        <li><strong>Key Highlights:</strong> Faced challenges from the East Asian financial crisis and sanctions post-Pokhran nuclear tests.</li>
        <li><strong>Outcome:</strong> Target Growth: 6.5%. Actual Growth: 5.4%.</li>
      </ul>

      <h3>Tenth Five Year Plan (2002 - 2007)</h3>
      <ul>
        <li><strong>Prime Minister:</strong> Atal Bihari Vajpayee / Manmohan Singh</li>
        <li><strong>Focus Area:</strong> Doubling per capita income in 10 years, massive employment creation.</li>
        <li><strong>Key Highlights:</strong> Emphasis on reducing gender gaps in literacy and wage rates. Significant reduction in poverty ratios.</li>
        <li><strong>Outcome:</strong> Successful. Target Growth: 8.0%. Actual Growth: 7.6%.</li>
      </ul>

      <h3>Eleventh Five Year Plan (2007 - 2012)</h3>
      <ul>
        <li><strong>Prime Minister:</strong> Manmohan Singh</li>
        <li><strong>Focus Area:</strong> "Faster and more inclusive growth."</li>
        <li><strong>Key Highlights:</strong> Massive push in agriculture and education (RTE Act passed). Survived the 2008 Global Financial Crisis relatively well.</li>
        <li><strong>Outcome:</strong> Target Growth: 9.0%. Actual Growth: 8.0% (impressive despite the global recession).</li>
      </ul>

      <h3>Twelfth Five Year Plan (2012 - 2017)</h3>
      <ul>
        <li><strong>Prime Minister:</strong> Manmohan Singh / Narendra Modi</li>
        <li><strong>Focus Area:</strong> "Faster, more inclusive, and sustainable growth."</li>
        <li><strong>Key Highlights:</strong> Focus on infrastructure development (target of $1 trillion investment), rural electrification, and improving health outcomes. It was the final five-year plan.</li>
      </ul>

      <h2>3. Quick Revision Comparison Table</h2>
      <table border={1} cellPadding={5} style={{ borderCollapse: 'collapse', width: '100%', marginBottom: '20px' }}>
        <thead>
          <tr>
            <th>Plan</th>
            <th>Period</th>
            <th>Core Objective</th>
            <th>Target vs Actual (%)</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>1st</td><td>1951-56</td><td>Agriculture & Irrigation</td><td>2.1 vs 3.6</td></tr>
          <tr><td>2nd</td><td>1956-61</td><td>Heavy Industries</td><td>4.5 vs 4.3</td></tr>
          <tr><td>3rd</td><td>1961-66</td><td>Self-reliance</td><td>5.6 vs 2.8</td></tr>
          <tr><td>4th</td><td>1969-74</td><td>Growth with stability</td><td>5.7 vs 3.3</td></tr>
          <tr><td>5th</td><td>1974-78</td><td>Poverty Eradication</td><td>4.4 vs 4.8</td></tr>
          <tr><td>6th</td><td>1980-85</td><td>Tech & Poverty</td><td>5.2 vs 5.7</td></tr>
          <tr><td>7th</td><td>1985-90</td><td>Food, Work, Productivity</td><td>5.0 vs 6.0</td></tr>
          <tr><td>8th</td><td>1992-97</td><td>Human Resource Dev.</td><td>5.6 vs 6.8</td></tr>
          <tr><td>9th</td><td>1997-02</td><td>Social Justice & Equity</td><td>6.5 vs 5.4</td></tr>
          <tr><td>10th</td><td>2002-07</td><td>Double Per Capita Income</td><td>8.0 vs 7.6</td></tr>
          <tr><td>11th</td><td>2007-12</td><td>Inclusive Growth</td><td>9.0 vs 8.0</td></tr>
          <tr><td>12th</td><td>2012-17</td><td>Sustainable Growth</td><td>8.2 (Target)</td></tr>
        </tbody>
      </table>

      <h2>4. Planning Commission vs NITI Aayog</h2>
      <p>
        In 2015, the NDA government replaced the 65-year-old Planning Commission with the National Institution for Transforming India (NITI Aayog). Why the change?
      </p>
      <ul>
        <li><strong>Approach:</strong> Planning Commission used a Top-Down approach (center dictates to states). NITI Aayog uses a Bottom-Up approach, emphasizing cooperative federalism.</li>
        <li><strong>Fund Allocation:</strong> Planning Commission had the power to allocate funds to ministries and state governments. NITI Aayog is solely a think tank; fund allocation is now the sole domain of the Finance Ministry.</li>
        <li><strong>State Representation:</strong> NITI Aayog's Governing Council includes all Chief Ministers, giving states a structural role in policy formulation, unlike the centralized Planning Commission.</li>
      </ul>

      <h2>5. Criticisms of the Five Year Plans</h2>
      <p>
        While planning built India's industrial backbone, it faced severe criticism:
      </p>
      <ul>
        <li><strong>License Raj:</strong> Heavy state control suffocated private enterprise until 1991.</li>
        <li><strong>Implementation Failures:</strong> Massive gaps between targets set and actual execution due to corruption and red tape.</li>
        <li><strong>Neglect of Agriculture post-1st Plan:</strong> The premature shift to heavy industries in the 2nd Plan worsened food security, forcing dependence on PL-480 wheat from the US.</li>
      </ul>

      <p>
        In conclusion, the era of Five Year Plans forms the bedrock of India's economic history. Understanding these phases allows us to comprehend how India transitioned from a colonial, agrarian economy to a globally integrated, rapidly growing economic powerhouse.
      </p>
    </>
  );
}


